
import { Options, Vue } from "vue-class-component";
import Tooltip from "@/app/ui/components/tooltip/index.vue";
import EmptyState from "@/app/ui/components/empty-list/index.vue";
import { AccountController } from "../../controllers/AccountController";
import debounce from "lodash/debounce";
import {
  downloadCsvFromCsvContent,
  ellipsisString,
  formatByCountry,
  resolveCountry
} from "@/app/infrastructures/misc/Utils";
import { CodIncomeController } from "../../controllers/CodIncomeController";
import ModalWithdraw from "@/app/ui/views/balance/component/modal-withdraw.vue";
import { BalanceController } from "../../controllers/BalanceController";
import { CodIncomeData } from "@/domain/entities/CodIncome";
import {
  BalanceTransactionType,
  TransactionTypeGober
} from "@/domain/entities/BalanceTransaction";
import { BalanceTransactionControllers } from "@/app/ui/controllers/BalanceTransactionControllers";
import { FlagsPermissionCodBalance } from "@/feature-flags/flags-permission-cod-balance";
import dateToDateString from "@/app/infrastructures/misc/common-library/v2/DateToDateString";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";

@Options({
  components: {
    Tooltip,
    EmptyState,
    ModalWithdraw
  }
})
class CodIncomeList extends Vue {
  get isVisibleViewDetail() {
    return FlagsPermissionCodBalance.permission_cod_balance_withdraw_view_detail.isEnabled();
  }

  async mounted() {
    this.options = await BalanceTransactionControllers.getBalanceTransactionType(
      "v1"
    ).then((res: TransactionTypeGober) => [
      new BalanceTransactionType({
        name: this.$t("codIncome.filterAll") as string,
        value: ""
      }),
      ...res.balanceCodClient
    ]);
    await BalanceController.getBalance(true);
  }

  unmounted() {
    CodIncomeController.onReset();
  }

  get getCurrentRoute() {
    return this.$route;
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get timeRangeShowOptions() {
    return `${this.$t("custom")}, ${this.$t("hari ini")}, ${this.$t(
      "kemarin"
    )}, ${this.$t("7 hari terakhir")}, ${this.$t("30 hari terakhir")}`;
  }

  get isOpenModalTopup() {
    return CodIncomeController.isOpenModalTopup;
  }

  get clientCurrency(): "IDR" | "MYR" {
    return (AccountController.accountData.countryCurrency || "IDR") as
      | "IDR"
      | "MYR";
  }

  get prefixCurrency() {
    return resolveCountry() === "MY" ? "RM" : "Rp";
  }

  formatMoney(value: number, prefixPositive = "") {
    const sign = value < 0 ? "- " : prefixPositive;
    return formatByCountry(Math.abs(value), {
      type: "money",
      prefix: `${sign}${this.prefixCurrency}`
    });
  }

  get codIncome() {
    return this.formatMoney(BalanceController.balanceData.walletBalanceCod);
  }

  get isOpenModalWithdraw() {
    return BalanceController.isOpenModalWithdraw;
  }

  openModalWithdraw() {
    if (!this.isDisableWithdraw) BalanceController.setOpenModalWithdraw(true);
  }

  closeModalWithdraw() {
    return BalanceController.setOpenModalWithdraw(false);
  }

  get isDisableWithdraw() {
    let isClientBranch = false;
    if (this.dataProfile.account_type === "client") {
      isClientBranch = this.dataProfile.account_type_detail.parentId !== 0;
    }
    return (
      BalanceController.balanceData.walletBalanceCod <= 0 || isClientBranch
    );
  }

  //tooltip
  isOpenTooltip = false;
  openTooltip = debounce((val: boolean) => {
    this.isOpenTooltip = val;
  }, 50);

  // search
  get search() {
    return CodIncomeController.search;
  }
  onSearch(value: string) {
    CodIncomeController.setSearch(value);
    CodIncomeController.setFirstPage();
    this.fetchCodIncomeList();
  }

  clearSearch() {
    CodIncomeController.setSearch("");
    this.fetchCodIncomeList();
  }

  // time range picker
  minDate: any = null;
  maxDate: any = null;
  isStartDate = true;
  setDay(day: any) {
    if (this.isStartDate) {
      this.minDate = new Date(
        new Date(day.id).setDate(new Date(day.id).getDate() - 89)
      );
      this.maxDate = new Date(
        new Date(day.id).setDate(new Date(day.id).getDate() + 89)
      );
      this.isStartDate = false;
    } else {
      this.endDate && (this.isStartDate = true);
    }
  }

  cancelRangePick() {
    if (!this.isStartDate) {
      this.minDate = null;
      this.maxDate = null;
      this.isStartDate = true;
    }
  }

  get startDate() {
    return CodIncomeController.filter.startDate;
  }

  get endDate() {
    return CodIncomeController.filter.endDate;
  }

  setDateRange(value: Array<any>) {
    this.minDate = null;
    this.maxDate = null;
    CodIncomeController.setStartDate(value[0]);
    CodIncomeController.setEndDate(value[1]);
    value[0] && value[1] && CodIncomeController.setFirstPage();
    value[0] && value[1] && this.fetchCodIncomeList();
  }

  // table
  get codIncomeList(): CodIncomeData[] {
    return CodIncomeController.codIncomeData.data;
  }

  get pagination() {
    return CodIncomeController.codIncomeData.pagination;
  }

  fetchCodIncomeList() {
    CodIncomeController.fetchCodIncomeList();
  }

  get isLoading() {
    return CodIncomeController.isLoading;
  }
  get errorCause() {
    return CodIncomeController.errorCause;
  }

  get columns() {
    return [
      {
        name: this.$t("codIncome.column.id"),
        styleHead: "text-black-lp-300 w-56 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize text-left break-all">${item.genesisTransactionId}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.transactionDate"),
        styleHead: "text-black-lp-300 w-48 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize overflow-ellipsis text-left">${this.formatDate(
            item.dateTransaction
          )}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.route"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          const route =
            item.originCountry && item.destinationCountry
              ? `${item.originCountry} - ${item.destinationCountry}`
              : "-";
          return `<div class="text-black-lp-300 text-left">${route}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.sttNo"),
        styleHead: "text-black-lp-300 w-48 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize text-left">${item.sttNumber}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.transactionType"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<p class="w-full break-all text-left">${this.getTrxNameByValue(
            item.type
          )}</p>`;
        }
      },
      {
        name: this.$t("codIncome.column.activity"),
        styleHead: "text-black-lp-300 w-28 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize overflow-ellipsis text-left">${item.activity}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.amount"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          const sign = item.activity === "credit" ? "+" : "-";
          const mainAmount = `<div class="font-medium text-left ${
            item.activity === "credit" ? "text-green-lp-100" : "text-red-lp-500"
          }">${sign}${formatPrice(
            item.amountTransaction,
            "",
            this.clientCurrency
          )}</div>`;
          const conversionAmount = item.destinationAmount
            ? `<div class="text-left text-black-lp-300">(${formatPrice(
                item.destinationAmount,
                "",
                item.destinationCurrency as "IDR" | "MYR"
              )})</div>`
            : "";
          return `<div class="capitalize flex flex-col">${mainAmount}${conversionAmount}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.balance"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="font-medium text-left">${formatPrice(
            item.lastBalance,
            "",
            this.clientCurrency
          )}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.note"),
        styleHead: "text-black-lp-300 w-64 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize text-left break-all">${
            item.note ? ellipsisString(item.note, 20) : "-"
          }</div>`;
        },
        vtooltip: (item: CodIncomeData) =>
          item.note?.length > 20 ? item.note : ""
      },
      {
        name: this.$t("codIncome.column.bankName"),
        styleHead: "text-black-lp-300 w-48 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize text-left break-words">${
            item.bankName ? item.bankName : "-"
          }</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.accountName"),
        styleHead: "text-black-lp-300 w-56 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize break-words text-left">${item.bankAccountName}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.accountNumber"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize overflow-ellipsis text-left">${item.bankAccountNumber}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.refId"),
        styleHead: "text-black-lp-300 w-52 text-left whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          return `<div class="text-black-lp-300 capitalize break-all text-left">${item.genesisReferenceId ||
            "-"}</div>`;
        }
      },
      {
        name: this.$t("codIncome.column.status"),
        styleHead: "text-black-lp-300 w-36 text-center whitespace-no-wrap",
        render: (item: CodIncomeData) => {
          const status = item.status.toLowerCase();
          return `<div class="flex justify-center"><span class="capitalize rounded-full px-2.5 py-0.5 text-12px font-medium ${this.handleClassPillStatus(
            status
          )}">${this.handleLabelPillStatus(item.status)}</span></div>`;
        }
      }
    ]
  };
  handleClassPillStatus(status: string) {
    if (status === "success") {
      return "bg-green-lp-300 text-green-lp-400";
    }
    if (status === "failed") {
      return "bg-red-lp-600 text-red-lp-100";
    }
    return "bg-yellow-lp-200 text-yellow-lp-100";
  }

  handleLabelPillStatus(status: string) {
    if (status === "success") {
      return this.$t("codIncome.status.success");
    }
    if (status === "failed") {
      return this.$t("codIncome.status.failed");
    }
    if (status === "request") {
      return this.$t("codIncome.status.request");
    }
    return status;
  }

  formatDate(date: string) {
    const locale = this.dataProfile.accountIsForeign ? "en" : "id";
    return `${dateToDateString(date, false, true, locale)}`;
  }

  // filter
  isOpenSelect = false;
  options = [] as BalanceTransactionType[];
  getTrxNameByValue(value: string) {
    const option = this.options.find((option: any) => {
      return option.value === value;
    });
    if (!option) {
      return value;
    }
    return option.name;
  }
  get advancedFilterData() {
    return CodIncomeController.advanceFilterData;
  }
  onSelect(value: any) {
    if (value.name === this.advancedFilterData.type.name) {
      CodIncomeController.setAdvanceFilterData({
        type: { name: "", value: "" }
      });
    } else {
      CodIncomeController.setAdvanceFilterData({
        type: { name: value.name, value: value.value }
      });
    }
    CodIncomeController.setFirstPage();
    this.fetchCodIncomeList();
  }

  async exportCod() {
    const resp = await CodIncomeController.exportCodTransaction({
      page: this.pagination.page,
      type: this.advancedFilterData.type.value,
      startDate: this.startDate
        ? new Date(this.startDate).toLocaleDateString("fr-CA")
        : "",
      endDate: this.endDate
        ? new Date(this.endDate).toLocaleDateString("fr-CA")
        : ""
    });

    if (resp.success) {
      downloadCsvFromCsvContent({
        fileName: "cod_income_mutation.csv",
        csvContent: resp.data
      });
    }
  }

  goToWithdraw() {
    this.$router.push("/cod-income/withdraw");
  }
}
export default CodIncomeList;
